import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTags } from "../context/TagsContext";
import { useFont } from "../context/FontContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Plus, X, GripVertical } from "lucide-react";

const ACTION_LABELS = {
  "project.create": "Created project",
  "project.update": "Updated project",
  "project.delete": "Deleted project",
  "entry.create": "Created entry",
  "entry.update": "Updated entry",
  "entry.delete": "Deleted entry",
  "user.role_change": "Changed role",
  "user.delete": "Deleted user",
  "user.password_reset": "Reset password",
  "admin.transfer": "Transferred admin",
  "tag.create": "Created tag",
  "tag.delete": "Deleted tag",
};

function AuditLogTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/audit-logs")
      .then((r) => setLogs(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const filtered = logs.filter((l) =>
    !filter || l.actor_name.toLowerCase().includes(filter.toLowerCase()) ||
    l.actor_email.toLowerCase().includes(filter.toLowerCase()) ||
    (ACTION_LABELS[l.action] || l.action).toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by actor or action..."
          className="border border-gray-200 px-4 py-2 text-sm w-80"
          data-testid="audit-filter"
        />
        <span className="text-xs text-gray-500 ml-auto">{filtered.length} / {logs.length} events</span>
      </div>
      <div className="border border-gray-200 overflow-x-auto">
        <table className="ess-table w-full min-w-[900px]">
          <thead>
            <tr>
              <th>When</th>
              <th>Actor</th>
              <th>Action</th>
              <th>Target</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="5" className="text-center py-6 text-gray-400">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan="5" className="text-center py-6 text-gray-400">No events.</td></tr>}
            {filtered.map((l) => (
              <tr key={l.id} data-testid={`audit-row-${l.id}`}>
                <td className="text-xs tabular-nums text-gray-500">{new Date(l.created_at).toLocaleString()}</td>
                <td>
                  <div className="font-medium text-[var(--ess-ink)]">{l.actor_name}</div>
                  <div className="text-xs text-gray-500">{l.actor_email}</div>
                </td>
                <td>
                  <span className="text-xs uppercase tracking-wider bg-gray-100 px-2 py-0.5">
                    {ACTION_LABELS[l.action] || l.action}
                  </span>
                </td>
                <td className="text-xs">{l.target_type}</td>
                <td className="text-xs text-gray-600">
                  {Object.entries(l.meta || {}).map(([k, v]) => (
                    <div key={k}><span className="text-gray-400">{k}:</span> {String(v)}</div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TagsTab() {
  const { tags, refreshTags, addTag, deleteTag } = useTags();
  const [newTag, setNewTag] = useState("");
  const [adding, setAdding] = useState("");
  const [deleting, setDeleting] = useState(null);

  const handleAdd = async () => {
    const name = newTag.trim();
    if (!name) return toast.error("Tag name is required");
    if (tags.some(t => t.toLowerCase() === name.toLowerCase())) {
      return toast.error("Tag already exists");
    }
    
    setAdding(name);
    try {
      await addTag(name);
      setNewTag("");
      toast.success(`Tag "${name}" added`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAdding("");
    }
  };

  const handleDelete = async (tag) => {
    if (!window.confirm(`Delete tag "${tag}"?\n\nNote: Existing time entries with this tag will not be affected, but you won't be able to select this tag for new entries.`)) {
      return;
    }
    
    setDeleting(tag);
    try {
      await deleteTag(tag);
      toast.success(`Tag "${tag}" deleted`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-lg font-semibold mb-1">Manage Tags</h3>
        <p className="text-sm text-gray-500 mb-4">
          Add or remove tags that users can select when logging time entries.
        </p>
        
        <div className="flex items-center gap-2 mb-6">
          <input
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="New tag name..."
            className="border border-gray-200 px-4 py-2.5 text-sm flex-1 max-w-sm"
            data-testid="new-tag-input"
            disabled={!!adding}
          />
          <button
            onClick={handleAdd}
            disabled={!newTag.trim() || !!adding}
            className="bg-[var(--ess-ink)] text-white px-4 py-2.5 text-sm font-semibold uppercase tracking-wider flex items-center gap-2 disabled:opacity-50"
            data-testid="add-tag-btn"
          >
            {adding === newTag.trim() ? (
              <>Adding...</>
            ) : (
              <><Plus size={14} /> Add Tag</>
            )}
          </button>
        </div>
      </div>

      <div className="border border-gray-200">
        <div className="px-4 py-2.5 bg-[var(--ess-surface)] border-b border-gray-200 flex items-center justify-between">
          <span className="text-xs uppercase tracking-wider text-gray-500">
            Active Tags ({tags.length})
          </span>
        </div>
        <div className="divide-y divide-gray-100">
          {tags.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-gray-400">
              No tags configured. Add your first tag above.
            </div>
          )}
          {tags.map((tag, index) => (
            <div 
              key={tag} 
              className="flex items-center justify-between px-4 py-3 hover:bg-gray-50"
              data-testid={`tag-row-${index}`}
            >
              <div className="flex items-center gap-3">
                <span className="text-gray-300">
                  <GripVertical size={14} />
                </span>
                <span className="text-sm text-[var(--ess-ink)] font-medium">{tag}</span>
              </div>
              <button
                onClick={() => handleDelete(tag)}
                disabled={deleting === tag}
                className="text-gray-400 hover:text-red-600 p-1 disabled:opacity-50"
                data-testid={`delete-tag-${index}`}
                title="Delete tag"
              >
                {deleting === tag ? (
                  <span className="text-xs">Deleting...</span>
                ) : (
                  <X size={16} />
                )}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 text-xs text-yellow-800">
        <strong>Note:</strong> Deleting a tag will remove it from the selection list for new entries. 
        Existing time entries that already have this tag will not be modified.
      </div>
    </div>
  );
}

function AppearanceTab() {
  const { font, loading, updateFont } = useFont();
  const [saving, setSaving] = useState(false);

    const fontOptions = [
    { name: "Barlow", description: "Clean, rounded sans-serif from Google", type: "Google" },
    { name: "Roboto", description: "Popular Google font, clean and modern", type: "Google" },
    { name: "Calibri", description: "Modern Microsoft Office default", type: "System" },
    { name: "Helvetica", description: "Classic Apple/Adobe sans-serif", type: "System" },
    { name: "sans-serif", description: "Browser's default sans-serif font", type: "Generic" },
  ];
	
  const handleSelectFont = async (fontName) => {
    setSaving(true);

    // Apply font visually immediately
    const fontFamily = fontName === "sans-serif" ? "sans-serif" : `'${fontName}', sans-serif`;
    document.documentElement.style.setProperty("--ess-font", fontFamily);
    document.body.style.fontFamily = fontFamily;

    // Save to backend using context function
    const success = await updateFont(fontName);
    
    if (!success) {
      // Revert visual change if save failed
      const fallbackFont = font === "sans-serif" ? "sans-serif" : `'${font}', sans-serif`;
      document.documentElement.style.setProperty("--ess-font", fallbackFont);
      document.body.style.fontFamily = fallbackFont;
    }
    
    setSaving(false);
  };

  const getPreviewStyle = (fontName) => {
    if (fontName === "sans-serif") {
      return "sans-serif";
    }
    return `'${fontName}', sans-serif`;
  };

												   
  if (loading) {
    return (
      <div className="py-12 text-center text-gray-400 text-sm">
        Loading appearance settings...
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-lg font-semibold mb-1">Appearance</h3>
      <p className="text-sm text-gray-500 mb-6">Choose a font for the application.</p>

      <div className="border border-gray-200">
        {fontOptions.map((fontOption) => (
          <button
            key={fontOption.name}
            onClick={() => handleSelectFont(fontOption.name)}
            disabled={saving}
            className={`w-full text-left px-6 py-5 flex items-center justify-between hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors ${
              font === fontOption.name ? "bg-blue-50 border-l-4 border-l-[var(--ess-ink)]" : "border-l-4 border-l-transparent"
            }`}
          >
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span 
                  className="text-lg font-semibold text-[var(--ess-ink)]" 
                  style={{ fontFamily: getPreviewStyle(fontOption.name) }}
                >
                  {fontOption.name}
                </span>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-gray-100 text-gray-600">
                  {fontOption.type}
                </span>
                {font === fontOption.name && (
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[var(--ess-ink)] text-white">
                    Active
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500">{fontOption.description}</p>
            </div>
            <div className="flex-shrink-0 ml-6 text-right">
              <div 
                className="text-xl text-gray-700 mb-1" 
                style={{ fontFamily: getPreviewStyle(fontOption.name) }}
              >
                Aa Bb Cc
              </div>
              <div 
                className="text-sm text-gray-400" 
                style={{ fontFamily: getPreviewStyle(fontOption.name) }}
              >
                123
              </div>
            </div>
          </button>
        ))}
      </div>

      {saving && (
        <div className="mt-4 text-sm text-gray-500 flex items-center gap-2">
          <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-[var(--ess-ink)] rounded-full"></div>
          Saving...
        </div>
      )}

      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 text-xs text-blue-800">
        <strong>Compatibility:</strong> All fonts work on all platforms. Barlow loads from Google Fonts (may take 1 second). Calibri is pre-installed on Windows; other platforms use fallback.
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("settings"); // settings | audit
  const [newAdmin, setNewAdmin] = useState("");
  const [loading, setLoading] = useState(false);

  if (user?.role !== "admin") {
    return <div className="p-8 text-gray-500">Admin only.</div>;
  }

  const transfer = async () => {
    if (!window.confirm(`Transfer admin rights to ${newAdmin}? You will be demoted to member.`)) return;
    setLoading(true);
    try {
      await api.post("/users/transfer-admin", { new_admin_email: newAdmin });
      toast.success("Admin transferred");
      await refresh();
      nav("/dashboard");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="admin-page">
      <div className="min-h-20 border-b border-gray-200 bg-white sticky top-0 z-20 flex items-center pl-16 lg:pl-8 pr-4 sm:pr-8 py-3">
        <div>
          <div className="ess-section-label">Admin</div>
          <div className="text-xl font-semibold tracking-tight text-[var(--ess-ink)]">Settings</div>
        </div>
      </div>

      <div className="px-4 sm:px-8 py-4 border-b border-gray-200 bg-[var(--ess-surface)] flex gap-1">
        <button onClick={() => setTab("settings")} className={`px-4 py-2 text-sm uppercase tracking-wider ${tab === "settings" ? "bg-[var(--ess-ink)] text-white" : "border border-gray-200"}`} data-testid="tab-settings">Settings</button>
        <button 
          onClick={() => setTab("appearance")} 
          className={`px-4 py-2 text-sm uppercase tracking-wider ${tab === "appearance" ? "bg-[var(--ess-ink)] text-white" : "border border-gray-200"}`}
          data-testid="tab-appearance">
          Appearance
        </button> 
        <button 
          onClick={() => setTab("tags")} 
          className={`px-4 py-2 text-sm uppercase tracking-wider ${tab === "tags" ? "bg-[var(--ess-ink)] text-white" : "border border-gray-200"}`}
          data-testid="tab-tags">
          Tags
        </button>
        <button onClick={() => setTab("audit")} className={`px-4 py-2 text-sm uppercase tracking-wider ${tab === "audit" ? "bg-[var(--ess-ink)] text-white" : "border border-gray-200"}`} data-testid="tab-audit">Audit Log</button>
      </div>

      <div className="px-4 sm:px-8 py-6 max-w-4xl">
        {tab === "settings" && (
            <div className="border border-gray-200 p-6 mb-6">
              <h3 className="text-lg font-semibold mb-1">Transfer admin rights</h3>
              <p className="text-sm text-gray-500 mb-4">Promote another team member to admin. You will become a member.</p>
              <input
                value={newAdmin}
                onChange={(e) => setNewAdmin(e.target.value)}
                placeholder="newadmin.essteam@gmail.com"
                className="w-full border border-gray-200 px-4 py-2.5 text-sm mb-3"
                data-testid="transfer-admin-email"
              />
              <button
                disabled={!newAdmin || loading}
                onClick={transfer}
                className="bg-[var(--ess-ink)] text-white px-4 py-2 text-sm font-semibold uppercase tracking-wider disabled:opacity-50"
                data-testid="transfer-admin-btn"
              >
                {loading ? "Transferring..." : "Transfer"}
              </button>
            </div>

            
          
        )}
        {tab === "appearance" && <AppearanceTab />}
        {tab === "tags" && <TagsTab />}
        {tab === "audit" && <AuditLogTab />}
      </div>
    </div>
  );
}
